import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { CAMPAIGN_REWARD_TYPE } from '../../../constants/enums';
import type { CashbackBonusFormValues } from '../../../schema';
import CashbackFreeSpinTiers, { createEmptyCashbackFreeSpinTier } from '../CashbackFreeSpinTiers';
import FormNumberInput from '../../molecules/FormNumberInput';

type Props = {
  disabled: boolean;
  aggregator?: 'GGA' | 'GOLDGAMES';
  marketplaceId?: string;
  currency?: string;
};

const RealMoneyRewardFields = ({ disabled }: Props) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<CashbackBonusFormValues>();

  return (
    <>
      <FormNumberInput
        control={control}
        name='refundPercentage'
        label='Refund percentage'
        error={errors.refundPercentage}
        slotProps={{ input: { endAdornment: '%' } }}
        required
        disabled={disabled}
      />
      <FormNumberInput
        control={control}
        name='minimumLostAmount'
        label='Minimum lost amount'
        error={errors.minimumLostAmount}
        required
        disabled={disabled}
      />
      <FormNumberInput
        control={control}
        name='maximumRefundAmount'
        label='Maximum refund amount'
        error={errors.maximumRefundAmount}
        required
        disabled={disabled}
      />
    </>
  );
};

const CashbackRewardFields = ({ disabled, aggregator = 'GGA', marketplaceId, currency }: Props) => {
  const { control, setValue } = useFormContext<CashbackBonusFormValues>();
  const [rewardType, freeSpinTiers] = useWatch({ control, name: ['rewardType', 'freeSpinTiers'] });
  const isFreeSpins = rewardType === CAMPAIGN_REWARD_TYPE.FREE_SPINS;
  const firstTierAggregator = freeSpinTiers[0]?.aggregator;

  useEffect(() => {
    if (!isFreeSpins || freeSpinTiers.length > 0) return;
    setValue('freeSpinTiers', [createEmptyCashbackFreeSpinTier(aggregator)], { shouldValidate: true });
  }, [freeSpinTiers.length, isFreeSpins, setValue, aggregator]);

  useEffect(() => {
    if (!isFreeSpins || disabled || !firstTierAggregator || firstTierAggregator === aggregator) return;
    setValue('freeSpinTiers', [createEmptyCashbackFreeSpinTier(aggregator)], { shouldValidate: true });
  }, [firstTierAggregator, isFreeSpins, setValue, aggregator, disabled]);

  return isFreeSpins ? (
    <CashbackFreeSpinTiers
      disabled={disabled}
      aggregator={aggregator}
      marketplaceId={marketplaceId}
      currency={currency}
    />
  ) : (
    <RealMoneyRewardFields disabled={disabled} />
  );
};

export default CashbackRewardFields;
